import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Button, Input, Modal } from 'antd'
import { TriangleAlert } from 'lucide-react'
import { useTRPC } from '@/trpc/react.ts'

type Order = { id: number; orderNo: string }
type Result = {
  successCount: number
  failedCount: number
  failures: { id: number; message: string }[]
  warnings?: string[]
}
type Props = {
  kind?: 'rental' | 'service'
  onClose: () => void
  onDeleted: () => Promise<void>
} & ({ order: Order; orders?: never } | { order?: never; orders: Order[] })

export function RentalForceDeleteModal({ order, orders, onClose, onDeleted, kind = 'rental' }: Props) {
  const [open, setOpen] = useState(true)
  const close = () => setOpen(false)
  const label = kind === 'rental' ? '租赁订单' : '服务订单'
  const targets = orders ?? [order]
  const batch = orders !== undefined
  const expected = batch ? `强制删除 ${targets.length} 个订单` : order.orderNo
  const [confirmation, setConfirmation] = useState('')
  const [reviewed, setReviewed] = useState(false)
  const [result, setResult] = useState<Result>()
  const trpc = useTRPC()
  const { message } = App.useApp()
  const remove = useMutation(trpc.order.rental.forceRemove.mutationOptions())
  const batchRemove = useMutation(trpc.order.rental.batchForceRemove.mutationOptions())
  const serviceRemove = useMutation(trpc.order.service.forceRemove.mutationOptions())
  const serviceBatchRemove = useMutation(trpc.order.service.batchForceRemove.mutationOptions())
  const pending = remove.isPending || batchRemove.isPending || serviceRemove.isPending || serviceBatchRemove.isPending
  const submit = async () => {
    if (confirmation.trim() !== expected || pending || targets.length === 0) {
      return
    }
    if (!reviewed) {
      setReviewed(true)
      return
    }
    let deleted = false
    try {
      if (batch) {
        const response =
          kind === 'service'
            ? await serviceBatchRemove.mutateAsync({
                orders: targets.map((target) => ({ serviceOrderId: target.id, confirmation: target.orderNo })),
                confirmation
              })
            : await batchRemove.mutateAsync({
                orders: targets.map((target) => ({ rentalOrderId: target.id, confirmation: target.orderNo })),
                confirmation
              })
        setResult(response)
        deleted = response.successCount > 0
      } else {
        const response =
          kind === 'service'
            ? await serviceRemove.mutateAsync({ serviceOrderId: order.id, confirmation })
            : await remove.mutateAsync({ rentalOrderId: order.id, confirmation })
        deleted = true
        if (response.warnings.length) {
          setResult({ successCount: 1, failedCount: 0, failures: [], warnings: response.warnings })
        } else {
          close()
          void message.success(`${label}及其关联数据已强制删除`)
        }
      }
    } catch (error) {
      setReviewed(false)
      void message.error(error instanceof Error ? error.message : '强制删除失败')
      return
    }
    if (deleted) {
      try {
        await onDeleted()
      } catch {
        void message.warning('订单已删除，列表刷新失败，请刷新页面')
      }
    }
  }
  let dialogTitle = `强制删除${label}`
  let confirmText = '核对无误，继续'
  if (reviewed) {
    dialogTitle = '第二次确认：永久删除'
    confirmText = '永久删除订单及关联记录'
  }
  if (result) {
    dialogTitle = '强制删除结果'
    confirmText = '关闭'
  }
  return (
    <Modal
      open={open}
      afterClose={onClose}
      centered
      width="32rem"
      classNames={{
        header: 'mb-4!',
        body: 'max-h-[65dvh] overflow-y-auto',
        footer: 'mt-5! border-t border-border pt-4'
      }}
      title={
        <div className="flex items-start gap-3 pr-6">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] text-(--danger)">
            <TriangleAlert aria-hidden size={20} />
          </span>
          <div>
            <div>{dialogTitle}</div>
            <p className="text-content-muted mt-1 text-xs font-normal">
              {result ? '请核对执行结果' : '高风险操作 · 仅供人工核对后执行'}
            </p>
          </div>
        </div>
      }
      okText={confirmText}
      cancelText="取消"
      onCancel={close}
      onOk={() => (result ? close() : void submit())}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {reviewed && !result && (
            <Button disabled={pending} onClick={() => setReviewed(false)}>
              返回核对
            </Button>
          )}
          {!result && <CancelBtn />}
          <OkBtn />
        </div>
      )}
      confirmLoading={pending}
      closable={!pending}
      mask={{ closable: !pending }}
      keyboard={!pending}
      cancelButtonProps={{ disabled: pending }}
      okButtonProps={{
        danger: !result,
        disabled: !result && (confirmation.trim() !== expected || pending || targets.length === 0)
      }}
    >
      {result ? (
        <div className="flex flex-col gap-4">
          <Alert
            showIcon
            type={result.failedCount || result.warnings?.length ? 'warning' : 'success'}
            title={`删除完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`}
          />
          <ul className="max-h-48 overflow-auto text-sm leading-6">
            {result.failures.map((failure) => (
              <li key={failure.id}>
                订单 #{failure.id}：{failure.message}
              </li>
            ))}
            {result.warnings?.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <section className="bg-surface-muted rounded-lg px-4 py-3">
            <div className="text-content-muted mb-2 flex justify-between text-xs">
              <span>本次删除</span>
              <span>
                {targets.length} 个{label}
              </span>
            </div>
            <ul className="max-h-28 overflow-y-auto text-sm leading-7 break-all">
              {targets.map((target) => (
                <li key={target.id}>
                  #{target.id} · {target.orderNo}
                </li>
              ))}
            </ul>
          </section>
          <div className="flex flex-col gap-2 text-sm leading-6">
            <p className="font-medium text-(--danger)">订单及关联数据将永久删除，无法恢复。</p>
            <p className="text-content-muted">
              永久删除订单及其账单、金额调整、收付款和退款分摊、归组明细与关联附件，包含正式和作废记录
              {kind === 'rental' ? '，以及合同和租赁占用' : ''}。
            </p>
            <p className="text-content-muted text-xs leading-5">
              仅处理所选订单的关联数据。共用资金流水保留其他账单对应的金额，共用账单组移除相关条目并重算汇总；无剩余关联的流水、账单组及不再被引用的附件一并删除。
            </p>
            {batch && <p className="text-content-muted text-xs">逐条执行，失败订单会单独列出原因。</p>}
          </div>
          <div className="border-border flex flex-col gap-2 border-t pt-4">
            {reviewed ? (
              <Alert
                type="error"
                showIcon
                title="请再次确认已完成人工核对"
                description={`点击“永久删除订单及关联记录”，立即删除以上 ${targets.length} 个订单及关联数据。`}
              />
            ) : (
              <>
                <label className="text-sm font-medium" htmlFor="rental-force-delete-confirmation">
                  {batch ? '输入下方文字，进入最终确认' : '输入完整订单号，进入最终确认'}
                </label>
                <p className="text-content-muted text-xs leading-5 break-all">{expected}</p>
                <Input
                  id="rental-force-delete-confirmation"
                  aria-label={batch ? '确认批量强制删除' : '确认强制删除的订单号'}
                  autoComplete="off"
                  placeholder={batch ? '输入确认文字' : '输入完整订单号'}
                  value={confirmation}
                  disabled={pending}
                  onChange={(event) => setConfirmation(event.target.value)}
                />
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
